import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useAuth } from './AuthContext.js';
import { useAppSocket, type EntityState } from '../lib/app-ws.js';
import { devices as devicesApi, bridges as bridgesApi, type Bridge } from '../lib/api.js';

interface DevicesContextValue {
  entities: Record<string, EntityState>;
  bridges: Bridge[];
  wsStatus: 'connecting' | 'open' | 'closed';
  patchEntity: (entityId: string, state: EntityState) => void;
  sendCommand: (
    domain: string,
    service: string,
    entityId?: string,
    params?: Record<string, unknown>,
  ) => Promise<void>;
  reload: () => void;
}

const DevicesContext = createContext<DevicesContextValue | null>(null);

export function DevicesProvider({ children }: { children: ReactNode }) {
  const { accessToken, currentHomeId, getToken, refresh } = useAuth();
  const [bridgeList, setBridgeList] = useState<Bridge[]>([]);
  const [reloadKey, setReloadKey] = useState(0);

  const {
    entities,
    status: wsStatus,
    patchEntity,
  } = useAppSocket(currentHomeId, getToken, refresh);

  useEffect(() => {
    if (!accessToken || !currentHomeId) return;
    devicesApi.list(currentHomeId).catch(() => {
      /* ws will populate */
    });
    bridgesApi
      .list(currentHomeId)
      .then((r) => setBridgeList(r.bridges))
      .catch(() => {
        /* ignore */
      });
  }, [accessToken, currentHomeId, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const sendCommand = useCallback(
    async (
      domain: string,
      service: string,
      entityId?: string,
      params?: Record<string, unknown>,
    ) => {
      if (!currentHomeId) throw new Error('No home selected');
      const online = bridgeList.find((b) => b.connected);
      const bridge = online ?? bridgeList[0];
      if (!bridge) throw new Error('No bridge available');
      await bridgesApi.sendCommand(currentHomeId, bridge.id, domain, service, entityId, params);
    },
    [currentHomeId, bridgeList],
  );

  return (
    <DevicesContext.Provider
      value={{
        entities,
        bridges: bridgeList,
        wsStatus,
        patchEntity,
        sendCommand,
        reload,
      }}
    >
      {children}
    </DevicesContext.Provider>
  );
}

export function useDevices(): DevicesContextValue {
  const ctx = useContext(DevicesContext);
  if (!ctx) throw new Error('useDevices must be used within DevicesProvider');
  return ctx;
}
